import { inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslationSandbox } from '@app/core/sandboxes/translation.sandbox';
import { AppConfigService } from '@app/core/services/config.service';
import { AuthService } from '@auth0/auth0-angular';
import { SurveySparrowIonicPlugin } from 'surveysparrow-ionic-plugin';
import { initializeSpotChecks, trackEvent, trackScreen } from 'surveysparrow-ionic-plugin/angular-ui';

export enum SpotchecksScreen {
    Dashboard = 'Dashboard',
    MyWork = 'MyWork',
    MyHousing = 'MyHousing',
    MyDocuments = 'MyDocuments',
    MyProfile = 'MyProfile',
    MyInbox = 'MyInbox',
    EventCalendar = 'EventCalendar',
}

export enum SpotchecksEvent {
}

@Injectable({
  providedIn: 'root',
})
export class SurveyService {
    private readonly translationSandbox = inject(TranslationSandbox);
    private readonly authService = inject(AuthService);
    private readonly configService = inject(AppConfigService);

    user = toSignal(this.authService.user$);
    isInitialized = signal(false);

    openSurvey(
        domain: string,
        token: string,
        params: Record<string, string> = {},
        properties: Record<string, string> = {}
    ): void {
        SurveySparrowIonicPlugin.loadFullScreenSurvey({ domain, token, params, properties });
    }

    initSpotChecks(): void {
        if (!this.configService.spotcheckDomain || !this.configService.spotcheckTargetToken) {
            return;
        }
        try {
            initializeSpotChecks({
                domainName: this.configService.spotcheckDomain,
                targetToken: this.configService.spotcheckTargetToken,
                variables: {
                    sparrowLang: this.translationSandbox.applicationLanguage()?.languageAlpha2Code,
                },
            });
            this.isInitialized.set(true);
        } catch (error) {
            console.error('Error initializing Spot Checks:', error);
            this.isInitialized.set(false);
        }
    }

    async trackScreen(screenName: string): Promise<void> {
        if (!this.isInitialized()) {
            console.warn('Spot Checks not initialized. Call initSpotChecks() before tracking screens.');
            return;
        }
        await trackScreen({
            screen: screenName,
            options: {
                variables: {
                    sparrowLang: this.translationSandbox.applicationLanguage()?.languageAlpha2Code,
                },
                userDetails: {
                    email: this.user().email,
                }
            },
        }).catch((error) => console.error('Error tracking screen:', error));
    }

    async trackEvent(screenName: string, eventName: string, eventData: Record<string, any>): Promise<void> {
        if (!this.isInitialized()) {
            console.warn('Spot Checks not initialized. Call initSpotChecks() before tracking events.');
            return;
        }
        await trackEvent({
            screen: screenName,
            event: {
                [eventName]: { ...eventData },
            },
        }).catch((error) => console.error('Error tracking event:', error));
    }
}
